import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { InputNumber, Select } from 'antd';
import { API_URL } from '../../config';
import type { Composicao, Item, Custo, Proposta } from './types';
import {
  getToken, fmt, modalOverlay,
  th, td, tdEdit, btnMini, inputStyle, inputStyleSm, labelStyle,
} from './utils';
import DescricaoComboBox from './DescricaoComboBox';
import ModalSelecionarServico from './ModalSelecionarServico';
import { PROPS_DINHEIRO_BR } from '../../utils/dinheiroInput';
import { calcularComposicao, chaveGrupo, quantidadeGrupoValida, trocarQuantidadeGrupo } from './composicaoCalculo';
import type { QuantidadesGrupos } from './composicaoCalculo';
import { serializarEstado } from '../comercial-propostas/fluxoPropostaComposicao';

// Editor full-screen da composição. Estado interno comporta:
// - itens (carregados via GET /composicoes/:id no mount)
// - grupos (1 por servico_id; + "Sem serviço" + grupos extras vazios)
// - calculadora de markup (ADM/ISS/Lucro)
// - atalhos: dias de campo, distância (km + pedágio), Alt+Q (adicionar item)
export default function EditorComposicao({ comp, custos: custosProp, propostas, servicos: servicosProp, markupBase, origemLabel, onClose, onSaved }: {
  comp: Composicao; custos: Custo[]; propostas: Proposta[];
  servicos: any[];
  markupBase: any | null;
  origemLabel?: string | null;
  onClose: () => void; onSaved: () => void | Promise<void>;
}) {
  // State local de serviços e custos (permite adicionar quando user cria pelo modal)
  const [servicos, setServicos] = useState<any[]>(servicosProp);
  useEffect(() => { setServicos(servicosProp); }, [servicosProp]);
  const [custos, setCustos] = useState<Custo[]>(custosProp);
  useEffect(() => { setCustos(custosProp); }, [custosProp]);

  const [itens, setItens] = useState<Item[]>([]);
  const [quantidadesGrupos, setQuantidadesGrupos] = useState<QuantidadesGrupos>({});
  const [erroCarregamento, setErroCarregamento] = useState(false);
  const [nome, setNome] = useState(comp.nome);
  const [descricao, setDescricao] = useState(comp.descricao || '');
  const [propostaId, setPropostaId] = useState(comp.proposta_id || '');
  const [markupPadrao, setMarkupPadrao] = useState(parseFloat(comp.markup_padrao_pct));
  const [status, setStatus] = useState(comp.status);
  const [isTemplate, setIsTemplate] = useState(!!comp.is_template);
  const [diasCampo, setDiasCampo] = useState<string>(comp.dias_campo ? String(comp.dias_campo) : '');
  const [distanciaKm, setDistanciaKm] = useState<string>('');
  const [atalhosAbertos, setAtalhosAbertos] = useState(false);
  const [observacoes, setObservacoes] = useState(comp.observacoes || '');
  // [#07d009bd] Calculadora colapsível pra liberar espaço pra tabela de itens.
  // Default fechada — usuário abre se quiser ajustar markup global.
  const [calculadoraAberta, setCalculadoraAberta] = useState(false);

  // Calculadora de markup local (sobrescreve base se editada)
  const cfg = comp.markup_config || {};
  const [admPct, setAdmPct] = useState<string>(
    cfg.adm_pct != null ? String(cfg.adm_pct)
    : markupBase?.adm?.pct != null ? markupBase.adm.pct.toFixed(2)
    : '17.3');
  const [issPct, setIssPct] = useState<string>(
    cfg.iss_pct != null ? String(cfg.iss_pct)
    : markupBase?.impostos?.iss != null ? String(markupBase.impostos.iss)
    : '2');
  const [lucroPct, setLucroPct] = useState<string>(
    cfg.lucro_pct != null ? String(cfg.lucro_pct) : '20');

  // Alíquotas federais fixas (Lucro Presumido — serviços, base de cálculo presumida 32%)
  const PIS = 0.65, COFINS = 3.0, IRPJ = 4.8, CSLL = 2.88;
  // Adicional IRPJ vem do backend (10% sobre o que excede 60k/trim na base presumida).
  const IRPJ_ADICIONAL = markupBase?.impostos?.irpj_adicional ?? 0;
  // REMVAR F4: pró-forma da comissão comercial (auto-extinguível, calculado no
  // backend; 0 enquanto o Lucas não ligar em comissao_regras.markup_proforma_pct).
  const COMISSAO_PCT = markupBase?.comissao_pct ?? 0;
  const adm = parseFloat(admPct) || 0;
  const iss = parseFloat(issPct) || 0;
  const lucro = parseFloat(lucroPct) || 0;
  const impostosTotal = PIS + COFINS + IRPJ + IRPJ_ADICIONAL + CSLL + iss;
  const somaMarkup = adm + impostosTotal + lucro + COMISSAO_PCT;
  // Markup = fator multiplicador (= preço/custo). Acréscimo % = (fator − 1) × 100.
  const markupFator = somaMarkup < 100 ? 1 / (1 - somaMarkup / 100) : 0;
  const acrescimoPct = (markupFator - 1) * 100;
  const markupValido = somaMarkup < 100 && markupFator > 0;

  function aplicarMarkupATodos() {
    if (!markupValido) {
      alert('Soma dos componentes ≥ 100%. Markup inválido — reduza as alíquotas ou o lucro.');
      return;
    }
    if (itens.length === 0) {
      alert('Adicione itens primeiro.');
      return;
    }
    if (!window.confirm(
      `Aplicar markup ${markupFator.toFixed(3)}× (acréscimo +${acrescimoPct.toFixed(2)}%) a TODOS os ${itens.length} itens?\n\n` +
      `Os valores atuais de "Acréscimo %" por item serão sobrescritos.`
    )) return;
    setItens(prev => prev.map(it => ({ ...it, markup_pct: parseFloat(acrescimoPct.toFixed(2)) })));
    setMarkupPadrao(parseFloat(acrescimoPct.toFixed(2)));
  }
  const [salvando, setSalvando] = useState(false);
  const [loadingItens, setLoadingItens] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/comercial/composicoes/${comp.id}`, { headers: { Authorization: token } });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        const quantidades = data.grupos_quantidades || data.markup_config?.grupos_quantidades || {};
        setQuantidadesGrupos(quantidades);
        setGruposExtras(Object.keys(quantidades).filter(chave => chave !== '__sem_servico__'));
        setItens((data.itens || []).map((it: any) => ({
          ...it,
          quantidade: parseFloat(it.quantidade) || 0,
          coeficiente: it.coeficiente != null ? parseFloat(it.coeficiente) : 1,
          valor_unitario: parseFloat(it.valor_unitario) || 0,
          markup_pct: parseFloat(it.markup_pct) || 0,
        })));
      } catch {
        setErroCarregamento(true);
      } finally { setLoadingItens(false); }
    })();
    // eslint-disable-next-line
  }, [comp.id]);

  // A composição é editada em estado local. Guardamos um retrato somente
  // depois que os itens terminam de carregar para distinguir uma edição real
  // de uma simples abertura da tela.
  const snapshotAtual = useMemo(() => serializarEstado({
    nome,
    descricao,
    propostaId: String(propostaId || ''),
    markupPadrao,
    status,
    isTemplate,
    diasCampo,
    observacoes,
    admPct,
    issPct,
    lucroPct,
    itens,
    quantidadesGrupos,
  }), [
    nome, descricao, propostaId, markupPadrao, status, isTemplate, diasCampo,
    observacoes, admPct, issPct, lucroPct, itens, quantidadesGrupos,
  ]);
  const snapshotInicialRef = useRef<{ composicaoId: string; valor: string } | null>(null);
  const [snapshotInicialPronto, setSnapshotInicialPronto] = useState(false);

  useEffect(() => {
    if (loadingItens || erroCarregamento) return;
    if (snapshotInicialRef.current?.composicaoId !== String(comp.id)) {
      snapshotInicialRef.current = { composicaoId: String(comp.id), valor: snapshotAtual };
    }
    setSnapshotInicialPronto(true);
  }, [comp.id, erroCarregamento, loadingItens, snapshotAtual]);

  const temAlteracoes = snapshotInicialPronto
    && snapshotInicialRef.current?.composicaoId === String(comp.id)
    && snapshotInicialRef.current.valor !== snapshotAtual;

  useEffect(() => {
    if (!temAlteracoes && !salvando) return;
    function protegerSaida(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', protegerSaida);
    return () => window.removeEventListener('beforeunload', protegerSaida);
  }, [salvando, temAlteracoes]);

  function confirmarDescarte(): boolean {
    return !temAlteracoes || window.confirm(
      'Descartar as alterações ainda não salvas desta composição?\n\n' +
      'Se você continuar, os ajustes feitos desde o último salvamento serão perdidos.'
    );
  }

  function tentarFechar() {
    if (salvando) return;
    if (confirmarDescarte()) onClose();
  }

  // Grupos extras (serviços sem nenhum item ainda) — exibidos como quadros vazios
  const [gruposExtras, setGruposExtras] = useState<string[]>([]);
  // Grupo atualmente em foco para receber novos itens via Alt+Q (último onde foi clicado +Item)
  const [grupoFoco, setGrupoFoco] = useState<string | null>(null);

  function addItem(servicoId: string | null = null) {
    setItens(prev => [...prev, {
      custo_unitario_id: null, servico_id: servicoId,
      descricao: '', unidade: 'un', quantidade: 0, coeficiente: 1,
      valor_unitario: 0, markup_pct: markupPadrao, ordem: prev.length,
    }]);
    setGrupoFoco(chaveGrupo(servicoId));
    // Se o grupo era extra (vazio), agora tem item — pode sair de gruposExtras
    if (servicoId) setGruposExtras(prev => prev.filter(s => s !== servicoId));
  }

  // Modal de seleção de serviço para "+ Novo grupo" (com busca)
  const [modalNovoGrupo, setModalNovoGrupo] = useState(false);

  function addNovoGrupo() {
    setModalNovoGrupo(true);
  }
  function confirmarNovoGrupo(servicoId: string) {
    setQuantidadesGrupos(prev => ({ ...prev, [servicoId]: 1 }));
    setGruposExtras(prev => prev.includes(servicoId) ? prev : [...prev, servicoId]);
    setGrupoFoco(servicoId);
    setModalNovoGrupo(false);
  }
  // Lista de serviços disponíveis (não usados ainda)
  const servicosDisponiveisParaGrupo = (() => {
    const usados = new Set<string>();
    itens.forEach(it => { if (it.servico_id) usados.add(it.servico_id); });
    gruposExtras.forEach(s => usados.add(s));
    return servicos.filter(s => !usados.has(s.id));
  })();

  function removerGrupo(servicoId: string | null) {
    const itensDoGrupo = itens.filter(it => (it.servico_id || null) === servicoId);
    if (itensDoGrupo.length > 0) {
      if (!window.confirm(`Remover este grupo? ${itensDoGrupo.length} item(ns) serão excluídos.`)) return;
      setItens(prev => prev.filter(it => (it.servico_id || null) !== servicoId));
    }
    if (servicoId) setGruposExtras(prev => prev.filter(s => s !== servicoId));
    setQuantidadesGrupos(prev => {
      const novas = { ...prev };
      delete novas[chaveGrupo(servicoId)];
      return novas;
    });
  }

  function mudarServicoDoGrupo(servicoIdAntigo: string | null, novoServicoId: string | null) {
    if (servicoIdAntigo === novoServicoId) return;
    // Verifica colisão com outro grupo já existente
    const jaExiste = itens.some(it => (it.servico_id || null) === novoServicoId)
                  || (novoServicoId && gruposExtras.includes(novoServicoId));
    const novasQuantidades = trocarQuantidadeGrupo(quantidadesGrupos,
      chaveGrupo(servicoIdAntigo), chaveGrupo(novoServicoId), !!jaExiste);
    if (!novasQuantidades) {
      alert('Esses grupos têm quantidades diferentes. Iguale as quantidades antes de mesclar os itens.');
      return;
    }
    if (jaExiste) {
      if (!window.confirm('Já existe um grupo para esse serviço. Mesclar os itens deste grupo no existente?')) return;
    }
    setQuantidadesGrupos(novasQuantidades);
    setItens(prev => prev.map(it =>
      (it.servico_id || null) === servicoIdAntigo ? { ...it, servico_id: novoServicoId } : it
    ));
    if (servicoIdAntigo) setGruposExtras(prev => prev.filter(s => s !== servicoIdAntigo));
    if (novoServicoId) {
      setGruposExtras(prev => prev.includes(novoServicoId) ? prev : [...prev, novoServicoId]);
    }
    setGrupoFoco(chaveGrupo(novoServicoId));
  }

  // Atalho de teclado: Alt+Q (mais fácil de digitar que Alt+I que conflitava com itálico)
  // adiciona um novo item ao grupo em foco
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (salvando) return;
      if (e.altKey && (e.key === 'q' || e.key === 'Q' || e.code === 'KeyQ')) {
        e.preventDefault();
        const sid = grupoFoco === '__sem_servico__' ? null : grupoFoco;
        addItem(sid);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markupPadrao, grupoFoco, salvando]);

  // Distância de viagem (ida+volta total) — aplica a itens com unidade "km" (combustível)
  // e calcula pedágio como 10% do custo total dos itens "km", se houver item de pedágio.
  function aplicarDistancia() {
    const km = parseFloat(distanciaKm) || 0;
    if (!km) { alert('Informe a distância em km primeiro.'); return; }
    const elegiveisKm = itens.filter(it => (it.unidade || '').toLowerCase() === 'km');
    const itensPedagio = itens.filter(it => /ped[áa]gio/i.test(it.descricao || ''));
    if (elegiveisKm.length === 0 && itensPedagio.length === 0) {
      alert('Nenhum item com unidade "km" nem item de pedágio encontrado.\n\nAdicione "Combustível" e/ou "Pedágio" do catálogo de custos.');
      return;
    }

    // Calcula custo total dos itens km que serão atualizados (após aplicar a distância)
    const totalCombustivelPrev = elegiveisKm.reduce(
      (s, it) => s + (km * (it.coeficiente ?? 1) * it.valor_unitario),
      0,
    );
    const pedagioCalc = totalCombustivelPrev * 0.10;

    let msg = '';
    if (elegiveisKm.length > 0) {
      msg += `${elegiveisKm.length} item(ns) "km" serão atualizados para quantidade = ${km}.\n`;
      msg += `Custo total combustível previsto: R$ ${totalCombustivelPrev.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n`;
    }
    if (itensPedagio.length > 0) {
      msg += `${itensPedagio.length} item(ns) de pedágio terão valor unitário ajustado para R$ ${pedagioCalc.toFixed(2)} (10% do combustível, qtd=1).\n\n`;
    }
    msg += 'Continuar?';
    if (!window.confirm(msg)) return;

    setItens(prev => prev.map(it => {
      const isKm = (it.unidade || '').toLowerCase() === 'km';
      const isPedagio = /ped[áa]gio/i.test(it.descricao || '');
      if (isKm) return { ...it, quantidade: km };
      if (isPedagio) return { ...it, quantidade: 1, valor_unitario: parseFloat(pedagioCalc.toFixed(2)) };
      return it;
    }));
  }

  // Aplica os "dias de campo" como quantidade nos itens com unidade "Diária".
  // Conta antes e pede confirmação se há valores manuais; só toca itens elegíveis.
  function aplicarDiasCampo() {
    const dias = parseFloat(diasCampo) || 0;
    if (!dias) { alert('Informe o número de dias de campo primeiro.'); return; }
    const elegiveis = itens.filter(it => (it.unidade || '').toLowerCase() === 'diária');
    if (elegiveis.length === 0) { alert('Nenhum item com unidade "Diária" para aplicar.'); return; }
    const comValor = elegiveis.filter(it => it.quantidade > 0 && it.quantidade !== dias);
    if (comValor.length > 0) {
      const ok = window.confirm(
        `${elegiveis.length} item(ns) "Diária" serão atualizados para quantidade = ${dias}.\n` +
        `${comValor.length} já têm quantidade diferente — esses valores serão sobrescritos.\n\nContinuar?`
      );
      if (!ok) return;
    }
    setItens(prev => prev.map(it =>
      (it.unidade || '').toLowerCase() === 'diária'
        ? { ...it, quantidade: dias }
        : it
    ));
  }
  const itensDiariaCount = itens.filter(it => (it.unidade || '').toLowerCase() === 'diária').length;
  const itensKmCount = itens.filter(it => (it.unidade || '').toLowerCase() === 'km').length;
  const itensPedagioCount = itens.filter(it => /ped[áa]gio/i.test(it.descricao || '')).length;

  // Navega para a proposta vinculada (Pipeline)
  function irParaProposta() {
    if (salvando) return;
    if (!confirmarDescarte()) return;
    if (origemLabel) {
      onClose();
      return;
    }
    if (!propostaId) return;
    sessionStorage.setItem('pipeline_destacar_proposta_id', String(propostaId));
    window.dispatchEvent(new CustomEvent('app-navigate', {
      detail: { modulo: 'comercial', pagina: 'com-pipeline' },
    }));
    onClose();
  }
  function selecionarCusto(idx: number, custoId: string) {
    const c = custos.find(x => x.id === custoId);
    if (!c) return;
    setItens(prev => prev.map((it, i) => i === idx ? {
      ...it,
      custo_unitario_id: c.id,
      descricao: c.descricao,
      unidade: c.unidade,
      valor_unitario: parseFloat(c.valor_unitario) || 0,
    } : it));
  }
  function atualizar(idx: number, k: string, v: any) {
    setItens(prev => prev.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  }
  function remover(idx: number) {
    setItens(prev => prev.filter((_, i) => i !== idx));
  }
  function mover(idx: number, dir: -1 | 1) {
    setItens(prev => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  }

  const calculo = calcularComposicao(itens, quantidadesGrupos);
  const { totalCusto, totalVenda } = calculo;
  const quantidadesValidas = Object.values(quantidadesGrupos).every(quantidadeGrupoValida);
  const margemBruta = totalVenda - totalCusto;
  const markupEfetivo = totalCusto > 0 ? (margemBruta / totalCusto) * 100 : 0;

  async function salvar() {
    if (loadingItens || erroCarregamento || !quantidadesValidas) return;
    setSalvando(true);
    try {
      const token = await getToken();
      // Salva metadata da composição
      await axios.put(`${API_URL}/comercial/composicoes/${comp.id}`, {
        nome, descricao, proposta_id: propostaId || null, is_template: isTemplate,
        markup_padrao_pct: markupPadrao, status,
        dias_campo: diasCampo === '' ? null : (parseFloat(diasCampo) || null),
        observacoes: observacoes || null,
        markup_config: {
          adm_pct: adm, iss_pct: iss, lucro_pct: lucro,
          impostos: { pis: PIS, cofins: COFINS, irpj: IRPJ, irpj_adicional: IRPJ_ADICIONAL, csll: CSLL, iss },
          impostos_total_pct: impostosTotal,
          soma_pct: somaMarkup,
          markup_fator: markupFator,
          acrescimo_pct: acrescimoPct,
        },
      }, { headers: { Authorization: token } });
      // Salva itens em lote (substitui todos)
      await axios.put(`${API_URL}/comercial/composicoes/${comp.id}/itens`, {
        itens, grupos_quantidades: quantidadesGrupos,
      }, {
        headers: { Authorization: token },
      });
      // O servidor calcula e grava o valor estimado junto com os itens e quantidades.
      // O callback pode ainda vincular esta composição ao documento de origem;
      // só fechamos o fluxo quando as duas etapas terminarem com sucesso.
      await onSaved();
      snapshotInicialRef.current = { composicaoId: String(comp.id), valor: snapshotAtual };
    } catch (e: any) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  // [eee79381] Tela cheia ao editar composição (mesmo padrão da edição de proposta).
  // Composição é uma estrutura grande (vários itens, grupos, calculadora de markup),
  // modal estreito 1180px ficava apertado. Tela cheia dá espaço pra trabalhar.
  const overlayFullscreen: React.CSSProperties = {
    ...modalOverlay,
    background: '#FAFBFF',
    alignItems: 'flex-start', justifyContent: 'flex-start',
  };
  const contentFullscreen: React.CSSProperties = {
    background: '#fff', borderRadius: 0, padding: 18,
    width: '100%', height: '100vh', maxWidth: 'none', maxHeight: '100vh',
    // Outer container não scrolla — o scroll fica no MAIN interno (flex: 1).
    // Footer com Total venda + Salvar fica sempre visível.
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
  };

  // Enquanto os itens chegam, nenhum campo editável é exibido. Isso impede
  // que uma digitação muito rápida seja confundida com o retrato original.
  if (loadingItens || !snapshotInicialPronto) {
    return (
      <div style={overlayFullscreen} onClick={onClose}>
        <div style={{ ...contentFullscreen, alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.stopPropagation()}>
          {erroCarregamento ? (
            <>
              <strong style={{ color: '#B91626', marginBottom: 10 }}>Não foi possível carregar a composição.</strong>
              <button onClick={onClose}
                style={{ padding: '8px 18px', fontSize: 13, border: '0.5px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
                Fechar
              </button>
            </>
          ) : (
            <div style={{ color: '#888', fontSize: 13 }}>Carregando composição...</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={overlayFullscreen} onClick={tentarFechar}>
      <div style={{ ...contentFullscreen, pointerEvents: salvando ? 'none' : undefined }}
        aria-busy={salvando} onClick={e => e.stopPropagation()}>
        {/* Header compacto: título + descrição (esq) | fechar (dir) */}
        {/* Proposta + Status + Template foram movidos pra linha de atalhos abaixo */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 8, flexShrink: 0 }}>
          {origemLabel && (
            <button type="button" onClick={tentarFechar} disabled={salvando}
              style={{
                padding: '7px 12px', fontSize: 12, fontWeight: 600,
                border: '0.5px solid #0930D0', borderRadius: 5,
                background: '#fff', color: '#0930D0', cursor: 'pointer',
                whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={`Voltar para ${origemLabel}`}>
              ← Voltar para {origemLabel}
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <input value={nome} onChange={e => setNome(e.target.value)}
              style={{ fontSize: 16, fontWeight: 600, color: '#020D7F', border: 'none', outline: 'none', width: '100%' }} />
            <input value={descricao} onChange={e => setDescricao(e.target.value)}
              placeholder="Descrição da composição..."
              style={{ fontSize: 12, color: '#888', border: 'none', outline: 'none', width: '100%', marginTop: 2 }} />
          </div>
          <button onClick={tentarFechar} disabled={salvando}
            style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '0 4px', flexShrink: 0 }}>×</button>
        </div>

        {/* Linha de atalhos: ⚡Atalhos · Proposta(searchable) · Status · spacer · +Grupo · +Item */}
        {/* Pedido do usuário: proposta e status na mesma linha que os atalhos. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
          flexWrap: 'wrap', flexShrink: 0 }}>
          <button type="button" onClick={() => setAtalhosAbertos(a => !a)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', fontSize: 11, fontWeight: 600,
              background: '#FFF8E8', border: '0.5px solid #B07500', borderRadius: 5,
              color: '#B07500', cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
            <span style={{ transition: 'transform 0.15s', display: 'inline-block', transform: atalhosAbertos ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
            ⚡ Atalhos
            {(diasCampo || distanciaKm) && !atalhosAbertos && (
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>
                ({diasCampo && `dias=${diasCampo}`}{diasCampo && distanciaKm && ' · '}{distanciaKm && `${distanciaKm}km`})
              </span>
            )}
          </button>

          {/* Proposta vinculada — combobox com busca por digitação */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>Proposta:</span>
            <Select
              showSearch
              allowClear
              size="small"
              value={propostaId || undefined}
              onChange={(v) => setPropostaId(v || '')}
              placeholder="— digite pra buscar —"
              style={{ minWidth: 240, maxWidth: 320 }}
              filterOption={(input, option) => {
                const txt = String(option?.label || option?.value || '').toLowerCase();
                return txt.includes(input.toLowerCase());
              }}
              options={propostas.map(p => ({ value: p.id, label: p.titulo }))}
            />
            {propostaId && (
              <button type="button" onClick={irParaProposta} disabled={salvando}
                title={origemLabel ? `Voltar para ${origemLabel}` : 'Abrir esta proposta no Pipeline'}
                style={{ padding: '2px 7px', fontSize: 11, borderRadius: 3,
                  border: '0.5px solid #0930D0', background: '#fff', color: '#0930D0',
                  cursor: 'pointer', fontWeight: 500 }}>↗</button>
            )}
          </div>

          {/* Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>Status:</span>
            <select value={status} onChange={e => setStatus(e.target.value as any)}
              style={{ ...inputStyleSm, minWidth: 110, width: 'auto', fontSize: 11, fontWeight: 600,
                color: status === 'ativa' ? '#0a7f4a' : status === 'arquivada' ? '#888' : '#B07500' }}>
              <option value="rascunho">Rascunho</option>
              <option value="ativa">Ativa</option>
              <option value="arquivada">Arquivada</option>
            </select>
          </div>

          {/* Template (movido do header pra junto do Status) */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
            fontSize: 11, color: '#B07500', fontWeight: 600, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={isTemplate} onChange={e => setIsTemplate(e.target.checked)} />
            📋 Template
          </label>

          {/* Contador de itens */}
          {itens.length > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>
              <strong style={{ color: '#020D7F' }}>{itens.length}</strong> item{itens.length !== 1 ? 's' : ''}
            </span>
          )}

          {/* + Novo grupo + Item */}
          <button onClick={addNovoGrupo} type="button"
            title="Adicionar um novo grupo (serviço) à composição."
            style={{ marginLeft: itens.length > 0 ? 0 : 'auto', padding: '5px 12px', fontSize: 12, borderRadius: 5, border: '0.5px solid #B07500',
                     background: '#fff', color: '#B07500', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            + Novo grupo
          </button>
          <button onClick={() => addItem(grupoFoco === '__sem_servico__' ? null : grupoFoco)} type="button"
            title={`Adicionar item ao grupo em foco (Alt+Q)`}
            style={{ padding: '5px 14px', fontSize: 12, borderRadius: 5, border: '0.5px solid #0930D0',
                     background: '#0930D0', color: '#fff', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            + Item <span style={{ fontSize: 9, opacity: 0.85, marginLeft: 4, fontWeight: 400 }}>Alt+Q</span>
          </button>
        </div>

        {/* Painel expandido de atalhos (dias de campo + distância) */}
        {atalhosAbertos && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px', marginBottom: 8,
            background: '#FFF8E8', border: '0.5px solid #B07500', borderRadius: 5, flexWrap: 'wrap',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 11, color: '#555' }} title="Aplica como quantidade nos itens com unidade 'Diária'">📅</span>
            <input type="number" step="1" min="0" value={diasCampo}
              onChange={e => setDiasCampo(e.target.value)}
              placeholder="dias"
              style={{ ...inputStyleSm, width: 60, textAlign: 'right' }} />
            <button onClick={aplicarDiasCampo} type="button"
              disabled={!diasCampo || itensDiariaCount === 0}
              title={`Aplicar a ${itensDiariaCount} item(ns) com unidade "Diária"`}
              style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                border: '0.5px solid #B07500', background: '#fff', color: '#B07500',
                cursor: (!diasCampo || itensDiariaCount === 0) ? 'not-allowed' : 'pointer',
                opacity: (!diasCampo || itensDiariaCount === 0) ? 0.5 : 1, fontWeight: 600, whiteSpace: 'nowrap' }}>
              → {itensDiariaCount}× Diária
            </button>
            <span style={{ color: '#ddd' }}>|</span>
            <span style={{ fontSize: 11, color: '#555' }} title="Aplica km nos itens com unidade 'km' e calcula pedágio (10% do combustível)">🚗</span>
            <input type="number" step="1" min="0" value={distanciaKm}
              onChange={e => setDistanciaKm(e.target.value)}
              placeholder="km"
              style={{ ...inputStyleSm, width: 70, textAlign: 'right' }} />
            <button onClick={aplicarDistancia} type="button"
              disabled={!distanciaKm || (itensKmCount === 0 && itensPedagioCount === 0)}
              title={`Aplicar a ${itensKmCount} item(ns) "km"${itensPedagioCount > 0 ? ` + ${itensPedagioCount} pedágio (10% do combustível)` : ''}`}
              style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                border: '0.5px solid #B07500', background: '#fff', color: '#B07500',
                cursor: (!distanciaKm || (itensKmCount === 0 && itensPedagioCount === 0)) ? 'not-allowed' : 'pointer',
                opacity: (!distanciaKm || (itensKmCount === 0 && itensPedagioCount === 0)) ? 0.5 : 1, fontWeight: 600, whiteSpace: 'nowrap' }}>
              → {itensKmCount}× km{itensPedagioCount > 0 ? ` + ${itensPedagioCount} ped.` : ''}
            </button>
          </div>
        )}

        <datalist id="unidades-comp">
          {['Diária','Unidade','Conjunto','km','h','mês','m','m²','m³','kg','ton','L','vb','pct'].map(u =>
            <option key={u} value={u} />)}
        </datalist>

        {/* ── MAIN scrollable (flex: 1) ────────────────────────────────────
            Tudo aqui dentro rola: grid de itens, totais detalhados, calculadora,
            observações. O footer (Total venda + Salvar) fica fixo abaixo,
            garantindo que o valor total e o botão Salvar estejam sempre visíveis.
            Prioridade: visualizar o máximo de linhas da composição. */}
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, paddingRight: 4 }}>

        {/* Grid de itens — quadros agrupados por serviço */}
        {erroCarregamento ? (
          <div role="alert" style={{ padding: 24, color: '#B91626' }}>
            Não foi possível carregar a composição. Feche e abra novamente para editar.
          </div>
        ) : loadingItens ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando itens...</div>
        ) : (() => {
          // Constrói grupos: 1 quadro por serviço (+ "Sem serviço" + grupos extras vazios)
          const ordemServicos: (string | null)[] = [];
          const itensPorGrupo = new Map<string, { item: Item; idx: number }[]>();
          itens.forEach((it, idx) => {
            const key = chaveGrupo(it.servico_id);
            if (!itensPorGrupo.has(key)) {
              itensPorGrupo.set(key, []);
              ordemServicos.push(it.servico_id || null);
            }
            itensPorGrupo.get(key)!.push({ item: it, idx });
          });
          const extras = [...gruposExtras];
          if (quantidadesGrupos.__sem_servico__ !== undefined) extras.push('__sem_servico__');
          extras.forEach(sid => {
            if (!itensPorGrupo.has(sid)) {
              itensPorGrupo.set(sid, []);
              ordemServicos.push(sid === '__sem_servico__' ? null : sid);
            }
          });
          if (ordemServicos.length === 0) {
            // Sem grupos ainda — mostra placeholder + botões
            return (
              <div style={{ padding: 40, textAlign: 'center', color: '#888', border: '1px dashed #ccc', borderRadius: 6 }}>
                <div style={{ marginBottom: 12, fontSize: 13 }}>Nenhum grupo/serviço ainda.</div>
                <div style={{ fontSize: 11, color: '#aaa' }}>
                  Clique em <strong>"+ Novo grupo"</strong> para criar um quadro por serviço, ou em <strong>"+ Item"</strong> para começar com itens sem serviço atribuído.
                </div>
              </div>
            );
          }
          return (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 10,
              // maxHeight removido: o scroll geral fica no MAIN wrapper.
              // O grid cresce naturalmente conforme o # de itens.
            }}>
              {ordemServicos.map(sid => {
                const key = chaveGrupo(sid);
                const lista = itensPorGrupo.get(key) || [];
                const servicoObj = sid ? servicos.find(s => s.id === sid) : null;
                const totaisGrupo = calculo.grupos.get(key);
                const quantidade = quantidadesGrupos[key] === undefined ? 1 : quantidadesGrupos[key];
                const isFoco = grupoFoco === key;
                return (
                  <div key={key} onClick={() => setGrupoFoco(key)}
                    style={{
                      border: isFoco ? '1.5px solid #0930D0' : '0.5px solid #e0e0e0',
                      borderRadius: 6, background: '#fff',
                      boxShadow: isFoco ? '0 0 0 2px rgba(9,48,208,0.08)' : 'none',
                    }}>
                    {/* Cabeçalho do grupo */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      padding: '4px 10px', background: sid ? '#E7ECFD' : '#F0F1F5',
                      borderBottom: '0.5px solid #e0e0e0', borderRadius: '6px 6px 0 0',
                    }}>
                      <strong style={{ fontSize: 12, color: '#020D7F', whiteSpace: 'nowrap' }}>
                        {sid ? '📦' : '📋'} Serviço:
                      </strong>
                      <select value={sid || ''}
                        onChange={e => mudarServicoDoGrupo(sid, e.target.value || null)}
                        title="Trocar o serviço deste grupo"
                        style={{
                          ...inputStyleSm, flex: '1 1 180px', maxWidth: 380, minWidth: 0, fontSize: 12, fontWeight: 600,
                          color: sid ? '#0930D0' : '#888',
                        }}>
                        <option value="">— sem serviço atribuído —</option>
                        {servicos.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.codigo ? `[${s.codigo}] ` : ''}{s.nome}
                          </option>
                        ))}
                      </select>
                      <span style={{ fontSize: 11, color: '#666' }}>
                        {lista.length} item(ns)
                      </span>
                      <span style={{ flex: 1 }} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                        Quantidade do grupo
                        <InputNumber {...PROPS_DINHEIRO_BR} min={0.0001} max={1000000}
                          precision={4} step={1} size="small" value={quantidade}
                          aria-label={`Quantidade do grupo ${servicoObj?.nome || 'Sem serviço'}`}
                          status={quantidadeGrupoValida(quantidade) ? undefined : 'error'}
                          onChange={valor => setQuantidadesGrupos(prev => ({ ...prev, [key]: valor }))}
                          style={{ width: 105 }} />
                        <span>{servicoObj?.unidade || 'vb'}</span>
                      </label>
                      <span style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>
                        Venda unitária: {fmt(totaisGrupo?.vendaUnitaria || 0)}
                      </span>
                      <span style={{ fontSize: 11, color: '#888' }}>Subtotal:</span>
                      <strong style={{ fontSize: 14, color: '#020D7F' }}>{fmt(totaisGrupo?.totalVenda || 0)}</strong>
                      <button onClick={(e) => { e.stopPropagation(); addItem(sid); }}
                        type="button"
                        title="Adicionar item a este grupo"
                        style={{
                          padding: '4px 10px', fontSize: 11, borderRadius: 4, border: '0.5px solid #0930D0',
                          background: '#fff', color: '#0930D0', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                        }}>+ Item</button>
                      <button onClick={(e) => { e.stopPropagation(); removerGrupo(sid); }}
                        type="button"
                        title="Remover este grupo (e seus itens)"
                        style={{
                          padding: '4px 8px', fontSize: 11, borderRadius: 4, border: '0.5px solid #B91626',
                          background: '#fff', color: '#B91626', cursor: 'pointer', fontWeight: 600,
                        }}>× Grupo</button>
                    </div>

                    <div style={{ padding: '5px 10px', fontSize: 11, color: '#666' }}>
                      Os itens abaixo compõem uma unidade do grupo. A quantidade do grupo multiplica o custo e a venda no total.
                    </div>
                    {/* Tabela de itens do grupo */}
                    {lista.length === 0 ? (
                      <div style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 11, fontStyle: 'italic' }}>
                        Nenhum item neste grupo. Clique em "+ Item" acima.
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#020D7F', color: '#fff' }}>
                            <th style={{ ...th, width: 30 }}></th>
                            <th style={th}>Descrição</th>
                            <th style={{ ...th, width: 70 }}>Unid.</th>
                            <th style={{ ...th, textAlign: 'right', width: 65 }}>Qtd.</th>
                            <th style={{ ...th, textAlign: 'right', width: 55 }} title="Multiplicador adicional (ex.: nº de pessoas)">Coef.</th>
                            <th style={{ ...th, textAlign: 'right', width: 90 }}>Valor unit.</th>
                            <th style={{ ...th, textAlign: 'right', width: 70 }} title="Acréscimo % sobre o custo">Acréscimo %</th>
                            <th style={{ ...th, textAlign: 'right', width: 105 }}>Valor total</th>
                            <th style={{ ...th, textAlign: 'center', width: 60 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lista.map(({ item: it, idx: i }, posLocal) => {
                            const total = it.quantidade * (it.coeficiente ?? 1) * it.valor_unitario * (1 + (it.markup_pct || 0) / 100);
                            const custoVinculado = it.custo_unitario_id ? custos.find(c => c.id === it.custo_unitario_id) : null;
                            return (
                              <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0', background: posLocal % 2 === 0 ? '#fff' : '#FAFBFF' }}>
                                <td style={{ ...td, textAlign: 'center', color: '#aaa', fontSize: 11 }}>{posLocal + 1}</td>
                                <td style={tdEdit}>
                                  <DescricaoComboBox
                                    item={it}
                                    custos={custos}
                                    onSelecionarCusto={c => selecionarCusto(i, c.id)}
                                    onChangeDescricao={v => atualizar(i, 'descricao', v)}
                                    onUnlink={() => atualizar(i, 'custo_unitario_id', null)}
                                    custoVinculado={custoVinculado || null}
                                    onCustoCriado={(novo) => setCustos(prev => [...prev, novo])}
                                  />
                                </td>
                                <td style={tdEdit}>
                                  <input list="unidades-comp" value={it.unidade} onChange={e => atualizar(i, 'unidade', e.target.value)} style={inputStyleSm} />
                                </td>
                                <td style={tdEdit}>
                                  <input type="number" step="0.01" value={it.quantidade}
                                    onChange={e => atualizar(i, 'quantidade', parseFloat(e.target.value) || 0)}
                                    style={{ ...inputStyleSm, textAlign: 'right' }} />
                                </td>
                                <td style={tdEdit}>
                                  <input type="number" step="0.01" value={it.coeficiente ?? 1}
                                    onChange={e => atualizar(i, 'coeficiente', parseFloat(e.target.value) || 0)}
                                    style={{ ...inputStyleSm, textAlign: 'right',
                                             background: (it.coeficiente ?? 1) !== 1 ? '#FFF8E8' : '#fff' }}
                                    title="Multiplicador adicional (default 1)" />
                                </td>
                                <td style={tdEdit}>
                                  <input type="number" step="0.01" value={it.valor_unitario}
                                    onChange={e => atualizar(i, 'valor_unitario', parseFloat(e.target.value) || 0)}
                                    style={{ ...inputStyleSm, textAlign: 'right' }} />
                                </td>
                                <td style={tdEdit}>
                                  <input type="number" step="0.01" value={it.markup_pct}
                                    onChange={e => atualizar(i, 'markup_pct', parseFloat(e.target.value) || 0)}
                                    style={{ ...inputStyleSm, textAlign: 'right' }} />
                                </td>
                                <td style={{ ...td, textAlign: 'right', fontWeight: 500, color: '#020D7F' }}>
                                  {fmt(total)}
                                </td>
                                <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                  <button onClick={() => mover(i, -1)} disabled={i === 0} style={btnMini} title="Mover pra cima">↑</button>
                                  <button onClick={() => mover(i, 1)} disabled={i === itens.length - 1} style={btnMini} title="Mover pra baixo">↓</button>
                                  <button onClick={() => remover(i)} style={{ ...btnMini, color: '#B91626' }} title="Remover">×</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* KPIs detalhados foram movidos pro footer (ao lado do Total venda) */}

        {/* Calculadora de markup — colapsível [#07d009bd] */}
        <div style={{
          marginTop: 14, padding: calculadoraAberta ? 12 : '8px 12px', borderRadius: 6,
          background: '#FAFBFF', border: '0.5px solid #CDD4EA',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: calculadoraAberta ? 10 : 0, cursor: 'pointer' }}
            onClick={() => setCalculadoraAberta(v => !v)}
            title={calculadoraAberta ? 'Recolher calculadora' : 'Abrir calculadora pra ajustar ADM/ISS/Lucro'}>
            <span style={{ fontSize: 11, color: '#888' }}>{calculadoraAberta ? '▼' : '▶'}</span>
            <strong style={{ fontSize: 13, color: '#020D7F' }}>🧮 Calculadora de markup</strong>
            <span style={{ fontSize: 10, color: '#888', flex: 1 }}>
              {calculadoraAberta
                ? 'Markup = 1 / (1 − ADM − Impostos − Lucro)'
                : `ADM ${adm.toFixed(1)}% · ISS ${iss.toFixed(1)}% · Lucro ${parseFloat(lucroPct || '0').toFixed(1)}% · Markup ${markupValido ? markupFator.toFixed(3) + '×' : 'inválido'}`}
            </span>
          </div>
          {calculadoraAberta && (<>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr) 1.4fr', gap: 10, alignItems: 'end' }}>
            <div>
              <label style={labelStyle}>ADM (%)</label>
              <input type="number" step="0.01" value={admPct}
                onChange={e => setAdmPct(e.target.value)} style={inputStyle} />
              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                {markupBase?.adm?.pct != null
                  ? `DRE 12m: ${markupBase.adm.pct.toFixed(2)}%`
                  : 'Indiretos / Receita 12m'}
              </div>
            </div>
            <div>
              <label style={labelStyle}>ISS (%)</label>
              <input type="number" step="0.01" value={issPct}
                onChange={e => setIssPct(e.target.value)} style={inputStyle} />
              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                Varia por município
              </div>
            </div>
            <div>
              <label style={labelStyle}>Lucro (%)</label>
              <input type="number" step="0.01" value={lucroPct}
                onChange={e => setLucroPct(e.target.value)} style={inputStyle} />
              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                Margem desejada
              </div>
            </div>
            <div style={{
              padding: '10px 14px', borderRadius: 5,
              background: markupValido ? '#0a7f4a15' : '#B9162615',
              border: `0.5px solid ${markupValido ? '#0a7f4a' : '#B91626'}`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Markup
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: markupValido ? '#0a7f4a' : '#B91626' }}>
                {markupValido ? markupFator.toFixed(3) + '×' : 'Inválido'}
              </div>
              <div style={{ fontSize: 10, color: '#888' }}>
                {markupValido
                  ? `+${acrescimoPct.toFixed(2)}% sobre custo · soma ${somaMarkup.toFixed(2)}%`
                  : `Soma ${somaMarkup.toFixed(2)}% (≥ 100%)`}
              </div>
            </div>
          </div>

          {/* Detalhamento dos impostos */}
          <details style={{ marginTop: 10 }}>
            <summary style={{ fontSize: 11, color: '#666', cursor: 'pointer' }}>
              Impostos detalhados (Lucro Presumido — serviços) — total {impostosTotal.toFixed(2)}%
            </summary>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginTop: 8, fontSize: 11 }}>
              {[
                { l: 'PIS', v: PIS, d: 'Receita bruta — cumulativo' },
                { l: 'COFINS', v: COFINS, d: 'Receita bruta — cumulativo' },
                { l: 'IRPJ', v: IRPJ, d: '32% presunção × 15%' },
                { l: 'IRPJ Adic.', v: IRPJ_ADICIONAL, d: '10% sobre (base presumida − R$ 60k/trim). Calculado sobre os 4 últimos trimestres.' },
                { l: 'CSLL', v: CSLL, d: '32% presunção × 9%' },
                { l: 'ISS', v: iss, d: 'Variável por município' },
              ].map(x => (
                <div key={x.l} title={x.d} style={{
                  padding: '6px 8px', background: '#fff', borderRadius: 4,
                  border: '0.5px solid #e0e0e0', cursor: 'help',
                  opacity: x.l === 'IRPJ Adic.' && x.v === 0 ? 0.5 : 1,
                }}>
                  <div style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>{x.l}</div>
                  <div style={{ fontSize: 13, color: '#020D7F', fontWeight: 600 }}>{x.v.toFixed(2)}%</div>
                </div>
              ))}
            </div>
            {markupBase?.irpj_adicional_detalhes && (
              <div style={{ fontSize: 10, color: '#888', marginTop: 6, fontStyle: 'italic' }}>
                Adicional IRPJ: 10% sobre excedente da base presumida (32% × receita) acima de R$ 60k/trim.
                {' '}Receita 12m: R$ {(markupBase.adm?.receita_total ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}.
              </div>
            )}
          </details>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button type="button" onClick={aplicarMarkupATodos}
              disabled={!markupValido || itens.length === 0}
              style={{
                padding: '7px 16px', fontSize: 12, fontWeight: 600, borderRadius: 5,
                border: '0.5px solid #0a7f4a',
                background: markupValido ? '#0a7f4a' : '#ddd',
                color: '#fff',
                cursor: (markupValido && itens.length > 0) ? 'pointer' : 'not-allowed',
                opacity: (markupValido && itens.length > 0) ? 1 : 0.6,
              }}>
              ↓ Aplicar markup {markupValido ? markupFator.toFixed(3) + '×' : ''} a todos os {itens.length} itens
            </button>
            <span style={{ fontSize: 10, color: '#888', flex: 1 }}>
              Sobrescreve o markup de cada item. O markup padrão da composição também é atualizado.
            </span>
          </div>
          </>)}
        </div>

        {/* Observações */}
        <div style={{ marginTop: 14 }}>
          <label style={{ ...labelStyle, fontSize: 11 }}>Observações da composição</label>
          <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
            placeholder="Notas internas, premissas adotadas, ressalvas comerciais..."
            style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontSize: 12 }} />
        </div>

        </div>
        {/* ── /MAIN scrollable ────────────────────────────────────────── */}

        {/* Footer fixo: KPIs compactos + Total venda destacado + Cancelar/Salvar. */}
        {!quantidadesValidas && <div role="alert" style={{ color: '#B91626', fontSize: 12, paddingTop: 8 }}>
          Informe uma quantidade maior que zero em cada grupo para salvar.
        </div>}
        <div style={{
          flexShrink: 0, display: 'flex', alignItems: 'stretch', gap: 10,
          flexWrap: 'wrap',
          marginTop: 10, paddingTop: 10, borderTop: '0.5px solid #e0e0e0',
        }}>
          {/* KPIs compactos — largura menor, ao lado do Total venda */}
          <div style={{
            padding: '8px 12px', background: '#F0F1F5', borderRadius: 6,
            display: 'flex', flexWrap: 'wrap', gap: 14,
            alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>Custo</div>
              <strong style={{ fontSize: 13, color: '#B91626', whiteSpace: 'nowrap' }}>{fmt(totalCusto)}</strong>
            </div>
            <div>
              <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>Margem</div>
              <strong style={{ fontSize: 13, color: '#0a7f4a', whiteSpace: 'nowrap' }}>{fmt(margemBruta)}</strong>
            </div>
            <div title={`Lucro estimado = Total venda × ${lucro.toFixed(2)}% (lucro da calculadora)`}>
              <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Lucro <span style={{ opacity: 0.7 }}>({lucro.toFixed(1)}%)</span>
              </div>
              <strong style={{ fontSize: 13, color: '#0a7f4a', whiteSpace: 'nowrap' }}>{fmt(totalVenda * lucro / 100)}</strong>
            </div>
            <div title="Markup efetivo = Total venda / Total custo">
              <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Markup <span style={{ opacity: 0.7 }}>+{markupEfetivo.toFixed(1)}%</span>
              </div>
              <strong style={{ fontSize: 13, color: '#0a7f4a', whiteSpace: 'nowrap' }}>
                {totalCusto > 0 ? (totalVenda / totalCusto).toFixed(3) : '—'}×
              </strong>
            </div>
          </div>

          {/* Total venda destacado — valor à direita */}
          <div style={{
            flex: '1 1 260px', padding: '10px 18px', borderRadius: 6, flexWrap: 'wrap',
            background: 'linear-gradient(135deg, #020D7F 0%, #0930D0 100%)',
            color: '#fff', display: 'flex', alignItems: 'center', gap: 14,
            boxShadow: '0 2px 12px rgba(9,48,208,0.18)',
          }}>
            <span style={{ fontSize: 11, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              Total venda
            </span>
            {propostaId && !isTemplate && (
              <span style={{ fontSize: 10, opacity: 0.85 }}>
                Sem proposta gerada: atualiza o negócio. Com proposta: reimporte os grupos nela.
              </span>
            )}
            <strong style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, marginLeft: 'auto' }}>
              {fmt(totalVenda)}
            </strong>
          </div>
          <button onClick={tentarFechar} disabled={salvando}
            style={{ padding: '9px 18px', fontSize: 13, border: '0.5px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
            {origemLabel ? 'Voltar sem salvar' : 'Cancelar'}
          </button>
          <button onClick={salvar} disabled={salvando || loadingItens || erroCarregamento || !quantidadesValidas}
            style={{ padding: '9px 22px', fontSize: 13, border: 'none', borderRadius: 6, background: '#0930D0', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {salvando ? 'Salvando...' : origemLabel ? `Salvar e voltar para ${origemLabel}` : 'Salvar composição'}
          </button>
        </div>
      </div>

      {modalNovoGrupo && (
        <ModalSelecionarServico
          servicos={servicosDisponiveisParaGrupo}
          onClose={() => setModalNovoGrupo(false)}
          onSelecionar={confirmarNovoGrupo}
          onCriado={(novo) => setServicos(prev => [...prev, novo])}
        />
      )}
    </div>
  );
}
